import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { useLatest } from 'react-use'
import { maxBy } from 'lodash-es'
import {
  Vector3,
  OrthographicCamera as OrthographicCameraThree,
  PerspectiveCamera as PerspectiveCameraThree,
  Box3,
} from 'three'
import { useSceneStore } from '@editor/stores'
import { useCameraStore } from '@modugen/ui-toolkit'
import { CameraControls, OrthographicCamera, PerspectiveCamera } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import { useAppStore } from '@stores'
import { applyLegacyCanvasZoomSteps } from './cypressCameraBridge'
import useFloorplanArrowPan from './useFloorplanArrowPan'

const defaultPerspectiveCameraPosition = new Vector3(-15, -15, 5)

const CameraControllerV2 = () => {
  const cameraEvents = useSceneStore(state => state.cameraEvents)

  const latestCameraEvent = useMemo<CameraEvents>(() => {
    const latestEvent = maxBy(Array.from(cameraEvents), event => event.event.priority)

    if (latestEvent) {
      return latestEvent.event
    }

    const event: FitToBoxPerspectiveCameraEvent = {
      cameraType: 'perspective',
      type: 'fitToBoxPerspective',
      box: { min: [0, 0, 0], max: [0, 0, 0] },
      // We do not care what the priority is for the default event
      priority: 0,
    }

    return event
  }, [cameraEvents])

  // ORTHOGRAPHIC CAMERA

  const camera = useThree(state => state.camera)
  const gl = useThree(state => state.gl)

  const isOrthographic = latestCameraEvent?.cameraType === 'orthographic'

  const rotateSpeed = isOrthographic ? 0 : 1.8

  const isLocked = useCameraStore(state => state.isLocked)
  const dollySpeed = useAppStore(state => state.dollySpeed)
  // const rotationTarget = useCameraStore(state => state.rotationTarget)
  // const orthoPosition = useCameraStore(state => state.orthoPosition)
  // const orthoZoom = useCameraStore(state => state.orthoZoom)

  const controlsRef = useRef<CameraControls | null>(null)
  const cameraRef = useLatest(camera)
  const glRef = useLatest(gl)
  const hasFloorplanEntry = useRef(false)

  useFloorplanArrowPan(controlsRef, cameraRef, isOrthographic && !isLocked, hasFloorplanEntry)

  /**
   * E2E-only bridge so Cypress/Playwright tests can zoom the scene without relying on synthetic `wheel` events.
   *
   * Why this lives here:
   * - We need access to the *live* `CameraControls` instance + the current R3F camera type (ortho vs perspective).
   * - `camera-controls` intentionally normalizes wheel deltas differently across platforms (macOS vs others),
   *   which makes `trigger('wheel', { deltaY })` flaky/non-deterministic on Linux CI.
   *
   * The E2E command calls into `applyLegacyCanvasZoomSteps`, which reproduces the same zoom/dolly math
   * but with deterministic normalization so `canvasZoom(n)` behaves the same everywhere. Cypress sets
   * `window.Cypress`; Playwright sets `window.__STATIK_E2E__` via an init script.
   */
  const syncE2ECameraBridge = useCallback(() => {
    if (typeof window === 'undefined' || !('Cypress' in window || '__STATIK_E2E__' in window))
      return

    const win = window as Window & {
      __STATIK_E2E_CAMERA__?: Window['__STATIK_E2E_CAMERA__']
    }

    const controls = controlsRef.current
    if (!controls) {
      delete win.__STATIK_E2E_CAMERA__
      return
    }

    win.__STATIK_E2E_CAMERA__ = {
      canvasZoomBySteps: (steps: number) =>
        applyLegacyCanvasZoomSteps(
          controls,
          cameraRef.current as OrthographicCameraThree | PerspectiveCameraThree,
          steps,
        ),
      projectModelPoint: (x: number, y: number, z: number) => {
        const liveCamera = cameraRef.current
        liveCamera.updateMatrixWorld()
        const projected = new Vector3(x, y, z).project(liveCamera)
        const canvas = glRef.current.domElement
        return {
          x: (projected.x + 1) * 0.5 * canvas.clientWidth,
          y: (-projected.y + 1) * 0.5 * canvas.clientHeight,
        }
      },
      getTarget: () => {
        const target = controls.getTarget(new Vector3(), true)
        return { x: target.x, y: target.y, z: target.z }
      },
    }
  }, [cameraRef, glRef])

  useLayoutEffect(() => {
    syncE2ECameraBridge()
  }, [camera, syncE2ECameraBridge])

  useEffect(() => {
    // to exclude objects from raycasting (see R3FNonSelectableObjectLayer in
    // the config) we use layers and therefore need to enable them all
    camera.layers.enableAll()
  }, [camera])

  useEffect(() => {
    if (camera instanceof PerspectiveCameraThree) {
      savedPerspectiveCameraPosition.current = camera.position.clone()
      controlsRef.current?.setPosition(-15, -15, 5)
    }
  }, [camera])

  const savedPerspectiveCameraPosition = useRef<Vector3>(defaultPerspectiveCameraPosition)

  const applyCameraEvent = useCallback(async () => {
    if (!controlsRef.current) return

    if (camera instanceof OrthographicCameraThree) {
      if (latestCameraEvent.type === 'fitToBoxOrthographic') {
        const box = new Box3(
          new Vector3(...latestCameraEvent.box.min),
          new Vector3(...latestCameraEvent.box.max),
        )
        const animate = false
        const opts = latestCameraEvent.options ?? {}
        const paddingLeft = opts.paddingLeft ?? 0
        const paddingRight = opts.paddingRight ?? 0
        const paddingTop = opts.paddingTop ?? 0
        const paddingBottom = opts.paddingBottom ?? 0

        // Z-up world: (0,0,1) is up. Looking from above = look down -Z; view plane is XY, X horizontal.
        const center = box.getCenter(new Vector3())
        const boxSizeX = box.max.x - box.min.x
        const boxSizeY = box.max.y - box.min.y
        const distance = Math.max(boxSizeX, boxSizeY, 1) * 2

        // Camera up = (0,1,0) so when looking down -Z, camera right = (-Z)×Y = X (screen horizontal = world X).
        camera.up.set(0, 1, 0)
        controlsRef.current?.updateCameraUp()

        await controlsRef.current?.setLookAt(
          center.x,
          center.y,
          center.z + distance,
          center.x,
          center.y,
          center.z,
          animate,
        )
        const viewW = camera.right - camera.left
        const viewH = camera.top - camera.bottom
        const zoomToFit = Math.min(
          viewW / (boxSizeX + paddingLeft + paddingRight),
          viewH / (boxSizeY + paddingTop + paddingBottom),
        )
        await controlsRef.current?.zoomTo(zoomToFit, animate)
        await controlsRef.current?.setFocalOffset(0, 0, 0, animate)
        // Home position for the `#` shortcut: the framing applied on entering the floorplan.
        controlsRef.current?.saveState()
        hasFloorplanEntry.current = true
      } else if (latestCameraEvent.type === 'fitToRectOrthographic') {
        const rectCenter = new Vector3(...latestCameraEvent.rectCenter)
        const normal = new Vector3(...latestCameraEvent.rectNormal).normalize()
        const viewW = camera.right - camera.left
        const viewH = camera.top - camera.bottom
        const zoomToFit = Math.min(
          viewW / latestCameraEvent.rectWidth,
          viewH / latestCameraEvent.rectLength,
        )
        const distance = Math.max(latestCameraEvent.rectWidth, latestCameraEvent.rectLength, 1)
        const cameraPosition = rectCenter.clone().add(normal.clone().multiplyScalar(-distance))

        controlsRef.current?.normalizeRotations()
        controlsRef.current?.zoomTo(zoomToFit, false)
        await controlsRef.current?.setLookAt(
          cameraPosition.x,
          cameraPosition.y,
          cameraPosition.z,
          rectCenter.x,
          rectCenter.y,
          rectCenter.z,
          false,
        )
      } else {
        // eslint-disable-next-line no-console
        console.log('Unexpected camera event type')
      }
    } else if (camera instanceof PerspectiveCameraThree) {
      hasFloorplanEntry.current = false
      const currentPosition = controlsRef.current?.getPosition(new Vector3())

      if (latestCameraEvent.type === 'fitToBoxPerspective') {
        const box = new Box3(
          new Vector3(...latestCameraEvent.box.min),
          new Vector3(...latestCameraEvent.box.max),
        )

        await controlsRef.current?.fitToBox(box, false, latestCameraEvent.options)
      } else if (latestCameraEvent.type === 'positionPerspective') {
        const position = controlsRef.current?.getPosition(new Vector3())

        await controlsRef.current?.setLookAt(
          position.x,
          position.y,
          position.z,
          latestCameraEvent.position[0],
          latestCameraEvent.position[1],
          latestCameraEvent.position[2],
          false,
        )
      } else if (latestCameraEvent.type === 'orientedFitPerspective') {
        const target = new Vector3(...latestCameraEvent.target)
        const right = new Vector3(...latestCameraEvent.right).normalize()
        // Re-orthogonalise `up` against `right` to guarantee an orthonormal basis even
        // when callers pass slightly off-axis vectors.
        const upInput = new Vector3(...latestCameraEvent.up)
        const up = upInput.sub(right.clone().multiplyScalar(upInput.dot(right))).normalize()
        // Viewer direction (target → camera): back = right × up. Do not mutate `camera.up` —
        // keep the global camera up axis so orbit / framing stays consistent with the rest of the scene.
        const back = right.clone().cross(up).normalize()

        const fovV = (camera.fov * Math.PI) / 180
        const aspect = camera.aspect
        const halfWidth = Math.max(latestCameraEvent.size.width, 1e-3) / 2
        const halfHeight = Math.max(latestCameraEvent.size.height, 1e-3) / 2
        const distanceForHeight = halfHeight / Math.tan(fovV / 2)
        const distanceForWidth = halfWidth / (aspect * Math.tan(fovV / 2))
        const padding = latestCameraEvent.padding ?? 1.4
        const distance = Math.max(distanceForHeight, distanceForWidth, 1) * padding

        const eye = target.clone().add(back.multiplyScalar(distance))

        await controlsRef.current?.setLookAt(
          eye.x,
          eye.y,
          eye.z,
          target.x,
          target.y,
          target.z,
          false,
        )
      } else {
        // eslint-disable-next-line no-console
        console.log('Unexpected camera event type')
      }

      savedPerspectiveCameraPosition.current = currentPosition ?? defaultPerspectiveCameraPosition
    } else {
      throw new Error('Camera is not an Orthographic or Perspective camera')
    }
  }, [camera, latestCameraEvent])

  useEffect(() => {
    applyCameraEvent()
  }, [applyCameraEvent, camera, latestCameraEvent])

  return (
    <>
      <CameraControls
        ref={node => {
          controlsRef.current = node
          syncE2ECameraBridge()
        }}
        makeDefault
        azimuthRotateSpeed={rotateSpeed}
        polarRotateSpeed={rotateSpeed}
        dollySpeed={dollySpeed}
        draggingSmoothTime={0}
        maxDistance={500}
        regress
        enabled={!isLocked}
        dollyToCursor={isOrthographic}
      />

      {isOrthographic ? <OrthographicCamera makeDefault /> : <PerspectiveCamera makeDefault />}
    </>
  )
}

export default CameraControllerV2
